import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import styled from 'styled-components';
import { billingService, BillingInfo } from '../../services/billingService';
import {
  colors,
  typography,
  spacing,
} from '../../design-system';

interface BillingSectionProps {
  // No props needed for now, but keeping the interface for consistency
}

const SectionTitle = styled.h3`
  margin: 0 0 ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.normal};
  color: ${colors.text.secondary};
`;

export const BillingSection: React.FC<BillingSectionProps> = () => {
  const [billingData, setBillingData] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    setLoading(true);
    try {
      const billing = await billingService.getCurrentSubscription();
      setBillingData(billing);
    } catch (error) {
      console.error('Error loading billing data:', error);
      // Don't show error message for billing as it's not critical
    } finally {
      setLoading(false);
    }
  };

  const handleBillingAction = async () => {
    try {
      if (billingData?.status === 'active') {
        // Redirect to Stripe customer portal for active subscriptions
        const portalSession = await billingService.createCustomerPortalSession();
        window.location.href = portalSession.url;
      } else {
        // Start subscription for users without active subscription
        const checkoutSession = await billingService.createCheckoutSession();
        window.location.href = checkoutSession.url;
      }
    } catch (error) {
      console.error('Billing action failed:', error);
      // message.error('Failed to process request. Please try again.');
    }
  };

  const handleBuyAiTokens = async () => {
    try {
      const session = await billingService.createAiAddonCheckout(1);
      window.location.href = session.url;
    } catch (error) {
      console.error('Failed to start AI tokens purchase:', error);
      // message.error('Could not start AI tokens purchase');
    }
  };

  const getBillingStatus = () => {
    if (loading) return 'Loading...';
    
    // Derive display from status
    switch (billingData?.status) {
      case 'active': return 'Active Plan';
      case 'trialing': return 'Free Trial';
      case 'expired': return 'No Plan';
      default: return 'No Plan';
    }
  };

  const getBillingDetails = () => {
    if (loading) return '';
    
    if (billingData?.status === 'active') {
      return 'Active subscription';
    } else if (billingData?.status === 'trialing') {
      return `Trial - ${billingData.trial_days_remaining || 0} days left`;
    } else {
      return 'No active subscription';
    }
  };

  const getActionButtonText = () => {
    if (loading) return '...';
    
    return billingData?.status === 'active' ? 'Manage' : 'Subscribe';
  };

  return (
    <div>
      <SectionTitle>Billing</SectionTitle>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ 
            fontSize: typography.fontSizes.base,
            fontWeight: typography.fontWeights.medium,
            color: colors.text.primary,
            marginBottom: spacing[1]
          }}>
            {getBillingStatus()}
          </div>
          <div style={{ 
            fontSize: typography.fontSizes.sm,
            color: colors.text.secondary
          }}>
            {getBillingDetails()}
          </div>
        </div>
        
        <button
          onClick={handleBillingAction}
          disabled={loading}
          style={{
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: '500',
            color: '#475569',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }
          }}
        >
          {getActionButtonText()}
        </button>
        <button
          onClick={handleBuyAiTokens}
          disabled={loading}
          style={{
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: '500',
            color: '#2563eb',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease',
            marginLeft: '8px'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.background = '#dbeafe';
              e.currentTarget.style.borderColor = '#93c5fd';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.background = '#eff6ff';
              e.currentTarget.style.borderColor = '#bfdbfe';
            }
          }}
        >
          Buy AI Tokens
        </button>
      </div>
    </div>
  );
};

export default BillingSection; 